import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ColumnVisibilityState } from '@tanstack/react-table';

import { apiErrorContent } from '@/components/atoms/ApiErrorMessage';
import { Spinner } from '@/components/atoms/Spinner';
import { ToastError } from '@/components/molecules/ToastTemplate/ToastTemplate';
import SomethingHappened from '@/components/SomethingHappened';
import { type CreateNewClientInput, useCreateClient, useGetClientSummaries } from '@/domain/client';
import { getFromStorage, setToStorage } from '@/utils/localStorage';

import ClientsView from './ClientsView';
import {
    COLUMN_VISIBILITY_STORAGE_KEY,
    DEFAULT_PAGE_SIZE,
    PAGE_SIZE_STORAGE_KEY,
    resolveColumnVisibility,
} from './clientListColumns';
import { type ClientStatusFilter, filterByStatus, needsAttention } from './clientStatus';

function ClientsContainer() {
    const { t } = useTranslation();
    const { data, isLoading, isFetching, refetch, error } = useGetClientSummaries();
    const { mutateAsync: createClient } = useCreateClient({
        onSuccess: () => refetch(),
    });

    const [status, setStatus] = useState<ClientStatusFilter>('active');
    const [filter, setFilter] = useState('');

    const [columnVisibility, setColumnVisibility] = useState<ColumnVisibilityState>(() =>
        resolveColumnVisibility(
            getFromStorage<ColumnVisibilityState>(COLUMN_VISIBILITY_STORAGE_KEY, {})
        )
    );
    useEffect(() => {
        setToStorage(COLUMN_VISIBILITY_STORAGE_KEY, columnVisibility);
    }, [columnVisibility]);

    const [pageSize, setPageSize] = useState<number>(() =>
        getFromStorage(PAGE_SIZE_STORAGE_KEY, DEFAULT_PAGE_SIZE)
    );
    useEffect(() => {
        setToStorage(PAGE_SIZE_STORAGE_KEY, pageSize);
    }, [pageSize]);

    const clients = data ?? [];
    const shown = useMemo(() => filterByStatus(data ?? [], status), [data, status]);
    const activeCount = clients.filter((client) => !client.archived).length;
    const attentionCount = clients.filter((client) => needsAttention(client)).length;

    // The add dialog has closed by now, so a failure is told in a toast.
    const onSubmitted = async (addClient: CreateNewClientInput) => {
        try {
            await createClient(addClient);
        } catch (error: unknown) {
            ToastError(
                apiErrorContent(error, t('internal.clients.createFailed')),
                'client-create-error'
            );
        }
    };

    if (isLoading) return <Spinner size="lg" fullScreen />;

    // The list carries the page's title, so the error state stands in as its h1.
    if (error) {
        return (
            <SomethingHappened
                headingLevel="h1"
                title={t('internal.clients.loadError')}
                onRetry={() => void refetch()}
                isRetrying={isFetching}
            />
        );
    }

    return (
        <ClientsView
            clients={shown}
            activeCount={activeCount}
            attentionCount={attentionCount}
            status={status}
            onStatusChange={setStatus}
            filter={filter}
            onFilterChange={setFilter}
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={setColumnVisibility}
            onColumnToggle={(columnId, visible) =>
                setColumnVisibility((prev) => ({ ...prev, [columnId]: visible }))
            }
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            onSubmitted={onSubmitted}
        />
    );
}

export default ClientsContainer;
